import Backdrop from '@material-ui/core/Backdrop';
import Modal from '@material-ui/core/Modal';
import Snackbar from '@material-ui/core/Snackbar';
import Slide from '@material-ui/core/Slide';
import MuiAlert from '@material-ui/lab/Alert';
import React, { useCallback, useMemo, useState } from 'react';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import {
  PublicApiKeyInfo,
  useCreatePublicApiKeyMutation,
  usePublicApiKey,
  useRegeneratePublicApiKeyMutation,
  useRevokePublicApiKeyMutation,
} from '../../../hooks/usePublicApiKey.ts';
import Button from '../../bits/Button.jsx';
import Switch from '../../bits/Switch.jsx';
import Tile from '../../bits/Tile.jsx';
import TileActionRow from '../../bits/TileActionRow.jsx';
import TileActionRowLink from '../../bits/TileActionRowLink.jsx';
import '../../bits/basic_dialog.scss';
import Loading from '../Loading.jsx';
import openExternalPage from '../../../services/openExternalPage';

const Alert: React.FC<any> = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return 'Never';
  }

  return new Date(value).toLocaleString();
};

const copyTextToClipboard = async (value: string, container: HTMLElement): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_error) {
      // Some browsers or webviews deny clipboard access; try a selected field.
    }
  }

  const previousFocus = document.activeElement as HTMLElement | null;
  const textArea = document.createElement('textarea');
  textArea.value = value;
  textArea.setAttribute('readonly', 'true');
  textArea.style.opacity = '0';
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '-9999px';

  try {
    // Keep the field inside Settings so the modal's focus trap allows selection.
    container.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, value.length);
    return document.execCommand('copy');
  } catch (_error) {
    return false;
  } finally {
    textArea.remove();
    previousFocus?.focus();
  }
};

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  confirmColor,
  loading,
  onCancel,
  onConfirm,
}) => (
  <Modal
    aria-labelledby="api-confirm-dialog-title"
    aria-describedby="api-confirm-dialog-description"
    className="basicDialog"
    open={open}
    closeAfterTransition
    BackdropComponent={Backdrop}
    BackdropProps={{
      timeout: 500,
      className: 'basicDialogBackdrop',
    }}
  >
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <div className="basicDialogWindow">
        <div id="api-confirm-dialog-title" className="basicDialogWindow_title">
          {title}
        </div>
        <div id="api-confirm-dialog-description" className="basicDialogWindow_content">
          {message}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            color="secondary"
            onClick={onCancel}
            disabled={loading}>Cancel</Button>
          <Button
            color={confirmColor}
            onClick={onConfirm}
            loading={loading}
            disabled={loading}>{confirmLabel}</Button>
        </div>
      </div>
    </Slide>
  </Modal>
);

const ApiAccessRoute: React.FC = () => {
  const { data, isLoading } = usePublicApiKey();
  const createMutation = useCreatePublicApiKeyMutation();
  const regenerateMutation = useRegeneratePublicApiKeyMutation();
  const revokeMutation = useRevokePublicApiKeyMutation();
  const [oneTimeKey, setOneTimeKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState<boolean>(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<'disable' | 'regenerate' | null>(null);
  const [message, setMessage] = useState<{ text: string; severity: string } | null>(null);

  useSetModalTitle('API Access');

  const isMutating = createMutation.isLoading || regenerateMutation.isLoading || revokeMutation.isLoading;

  const keyDescription = useMemo(() => {
    if (!data?.available) {
      return 'Unavailable';
    }

    if (!data?.enabled) {
      return '';
    }

    return data.keyPreview || 'Enabled';
  }, [data]);

  const handleResponse = useCallback((response: PublicApiKeyInfo) => {
    if (response.key) {
      setOneTimeKey(response.key);
      setKeyCopied(false);
    }
  }, []);

  const onEnable = useCallback(() => {
    createMutation.mutate(undefined, {
      onSuccess: response => handleResponse(response),
      onError: () => setMessage({ text: 'Unable to enable API access.', severity: 'error' }),
    });
  }, [createMutation, handleResponse]);

  const regenerateApiKey = useCallback(() => {
    regenerateMutation.mutate(undefined, {
      onSuccess: response => {
        setPendingConfirmation(null);
        handleResponse(response);
      },
      onError: () => setMessage({ text: 'Unable to regenerate API key.', severity: 'error' }),
    });
  }, [regenerateMutation, handleResponse]);

  const disableApiAccess = useCallback(() => {
    revokeMutation.mutate(undefined, {
      onSuccess: () => {
        setPendingConfirmation(null);
        setOneTimeKey(null);
        setKeyCopied(false);
      },
      onError: () => setMessage({ text: 'Unable to disable API access.', severity: 'error' }),
    });
  }, [revokeMutation]);

  const onEnabledChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setPendingConfirmation(null);
      onEnable();
      return;
    }

    setPendingConfirmation('disable');
  }, [onEnable]);

  const onRegenerate = useCallback(() => {
    setPendingConfirmation('regenerate');
  }, []);

  const onCancelConfirmation = useCallback(() => {
    setPendingConfirmation(null);
  }, []);

  const onConfirmPendingAction = useCallback(() => {
    if (pendingConfirmation === 'disable') {
      disableApiAccess();
      return;
    }

    if (pendingConfirmation === 'regenerate') {
      regenerateApiKey();
    }
  }, [disableApiAccess, pendingConfirmation, regenerateApiKey]);

  const onCopyKey = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const container = event.currentTarget.parentElement;

    if (!oneTimeKey || !container) {
      return;
    }

    const copied = await copyTextToClipboard(oneTimeKey, container);
    if (copied) {
      setKeyCopied(true);
      window.setTimeout(() => setKeyCopied(false), 2000);
      return;
    }

    setMessage({ text: 'Unable to copy API key.', severity: 'error' });
  }, [oneTimeKey]);

  const onHideMessage = useCallback(() => setMessage(null), []);

  const isMobile = !!window.cordova || process.env.PLATFORM === 'mobile';
  const onOpenDocumentation = useCallback(() => {
    try {
      openExternalPage(new URL('/docs/api', API_BASE_URL).href);
    } catch (_error) {
      setMessage({ text: 'Unable to open API documentation. Please restart the app and try again.', severity: 'error' });
    }
  }, []);

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && data &&
        <div>
          <Tile title="API Key">
            <div className="sgpsTile__actionRow" style={{ display: 'block' }}>
              <div style={{ alignItems: 'flex-start', display: 'flex' }}>
                <div className="sgpsTile__actionRow__main">
                  <div className="sgpsTile__actionRow__label">Enabled</div>
                  {keyDescription &&
                    <div className="sgpsTile__actionRow__description">{keyDescription}</div>
                  }
                </div>
                <div className="sgpsTile__actionRow__icon">
                  <Switch
                    name="publicApiKeyEnabled"
                    className="up-8"
                    edge="end"
                    onChange={onEnabledChange}
                    checked={data.available && data.enabled}
                    disabled={!data.available || isMutating}
                    inputProps={{ 'aria-labelledby': 'switch-list-label-public-api-key' }}
                  />
                </div>
              </div>
              {oneTimeKey &&
                <div style={{ marginTop: '14px' }}>
                  <div className="sgpsTile__actionRow__description">
                    Copy this key now. It will not be visible again after you leave this screen.
                  </div>
                  <div style={{
                    alignItems: 'center',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    display: 'flex',
                    gap: '8px',
                    marginTop: '8px',
                    padding: '10px 12px',
                  }}>
                    <code style={{
                      color: '#4A4A4A',
                      flex: 1,
                      overflowWrap: 'anywhere',
                      userSelect: 'text',
                    }}>{oneTimeKey}</code>
                    <button
                      type="button"
                      aria-label="Copy API key"
                      onClick={onCopyKey}
                      style={{
                        alignItems: 'center',
                        background: 'transparent',
                        border: 'none',
                        color: '#4A4A4A',
                        cursor: 'pointer',
                        display: 'flex',
                        flex: '0 0 auto',
                        fontSize: '18px',
                        height: '32px',
                        justifyContent: 'center',
                        padding: 0,
                        width: '32px',
                      }}
                    >
                      <i className={`fa ${keyCopied ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </div>
                </div>
              }
            </div>
            {data.available && data.enabled &&
              <>
                <TileActionRow
                  label="Created"
                  description={formatDate(data.createdAt)}
                />
                <TileActionRow
                  label="Last used"
                  description={formatDate(data.lastUsedAt)}
                />
              </>
            }
          </Tile>

          <Tile>
            <TileActionRowLink
              type="external"
              label="API Documentation"
              to="/docs/api"
              onClick={isMobile ? onOpenDocumentation : undefined}
            />
          </Tile>

          {data.available && data.enabled &&
            <Button
              color="primary"
              onClick={onRegenerate}
              loading={regenerateMutation.isLoading}
              disabled={isMutating}>Regenerate API key</Button>
          }
        </div>
      }

      <Snackbar
        open={message !== null}
        autoHideDuration={6000}
        onClose={onHideMessage}
      >
        <Alert onClose={onHideMessage} severity={message?.severity || 'success'}>
          {message?.text || ''}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={pendingConfirmation !== null}
        title={pendingConfirmation === 'disable' ? 'Disable API access?' : 'Regenerate API key?'}
        message={pendingConfirmation === 'disable'
          ? 'The existing API key will stop working.'
          : 'The existing API key will stop working and a new key will be shown once.'}
        confirmLabel={pendingConfirmation === 'disable' ? 'Disable' : 'Regenerate'}
        confirmColor={pendingConfirmation === 'disable' ? 'danger' : 'primary'}
        loading={pendingConfirmation === 'disable' ? revokeMutation.isLoading : regenerateMutation.isLoading}
        onCancel={onCancelConfirmation}
        onConfirm={onConfirmPendingAction}
      />
    </>
  );
};

export { ApiAccessRoute };
